/**
 * Client-side read of the non-production `ld_preview_force` cookie, so an e2e
 * spec can pin the arm of a client-only (posthog-js) experiment per test.
 *
 * Every e2e lane runs posthog-js unloaded (blank key / localhost), so a
 * `useExperiment` flag never resolves there and any surface behind one is
 * unreachable by Playwright. 2026-11-04: the ad wall (`ad-wall-placement`)
 * swapped its sponsor for the house card mid-countdown in production and no
 * e2e could open the wall to prove the fix (#8009).
 *
 * Same cookie and `flagKey=variant` token syntax as the server-side
 * `previewForcedVariant` (src/lib/flags/preview-force.ts). The browser cannot
 * read the worker's ENVIRONMENT, so production is excluded by two gates that
 * both fail closed:
 *  1. posthog-js loaded ⇒ no override. Production always loads it.
 *  2. the page is served from lokerdollar.com or a subdomain ⇒ no override.
 * Same gates as the per-experiment readers that predate this
 * (`readAhaSignupAskE2eOverride`, `readPostApplyTrackPromptE2eOverride`).
 */
export const CLIENT_PREVIEW_FORCE_COOKIE = "ld_preview_force";

export function readClientForcedVariant(
	flagKey: string,
	posthogLoaded: boolean,
): string | null {
	if (posthogLoaded) return null;
	try {
		const host = window.location.hostname;
		if (host === "lokerdollar.com" || host.endsWith(".lokerdollar.com")) {
			return null;
		}
		const raw = document.cookie
			.split(";")
			.map((c) => c.trim())
			.find((c) => c.startsWith(`${CLIENT_PREVIEW_FORCE_COOKIE}=`));
		if (!raw) return null;
		const tokens = decodeURIComponent(
			raw.slice(CLIENT_PREVIEW_FORCE_COOKIE.length + 1),
		)
			.split(",")
			.map((t) => t.trim());
		for (const token of tokens) {
			const sep = token.indexOf("=");
			if (sep <= 0 || token.slice(0, sep).trim() !== flagKey) continue;
			const variant = token.slice(sep + 1).trim();
			return variant || null;
		}
	} catch {
		/* no window / cookie access: no override */
	}
	return null;
}
